import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  Injectable,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '@core/authentication/auth.service';
import { OiqClientCreateFormComponent } from '@v2/modules/clients/components/client-create/client-form.component';
import { OiqClientSearchHistoryComponent } from '@v2/modules/clients/components/client-search/client-search-history.component';
import { OiqClientSearchHistoryEvent } from './client-search-dialog.models';

@Component({
  selector: 'oiq-client-search-dialog',
  templateUrl: './client-search-dialog.component.html',
  styleUrls: ['./client-search-dialog.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    ReactiveFormsModule,
    OiqClientCreateFormComponent,
    OiqClientSearchHistoryComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OiqClientSearchDialogComponent {
  public createFormRef = viewChild(OiqClientCreateFormComponent);

  private authService = inject(AuthService);
  public data = inject(DIALOG_DATA);
  public whiteLabels = this.data?.whiteLabels || [];
  public whiteLabel = this.whiteLabels.find(wl => wl.id === this.data?.whiteLabelId);

  public dialogRef = inject<DialogRef<any, OiqClientSearchDialogComponent>>(DialogRef);

  // Form and state management
  addingClient = signal<boolean>(false);
  private _searchText: string = '';
  get searchWhitelabelId(): number | undefined {
    const tenantId = this.authService.getUser().tenantId;
    return tenantId == 1 ? undefined : this.data?.whiteLabelId;
  }

  constructor() {
    effect(() => {
      const _createFormRef = this.createFormRef();
      if (!_createFormRef) {
        return;
      }

      _createFormRef.formRef.get('name').setValue(this._searchText);
    });
    // this.form.configuration = { whiteLabelId: this.data?.whiteLabelId };
    this.addingClient.set(this.data && this.data.addingClient);
  }

  private _closeDialog(action: string, opts: { client: any; application?: any; funding?: any }) {
    this.dialogRef.close({
      action: action,
      ...opts,
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  submitNewClient(): void {
    const _createFormRef = this.createFormRef();
    if (!_createFormRef) {
      return;
    }
    _createFormRef.formRef.doCreate().subscribe({
      next: (_client: any) => {
        this.dialogRef.close({
          action: 'new',
          client: _client,
        });
      },
      error: (error: any) => {},
    });
  }

  handleHistoryEvent(event: OiqClientSearchHistoryEvent): void {
    switch (event.action) {
      case 'search':
        this._searchText = event.data as string;
        break;
      case 'create':
        this.addingClient.set(true);
        break;
      case 'select':
        this._closeDialog('new', event.data as any);
        break;
      case 'decline':
      case 'renew':
      case 'resubmit':
      case 'renewResubmit':
        this._closeDialog(event.action, event.data as any);
        break;
    }
  }

  handleFormEvent(event: { type: string; data?: any }): void {
    switch (event.type) {
      case 'back_clicked':
        this.addingClient.set(false);
        break;
    }
  }
}

@Injectable({ providedIn: 'root' })
export class OiqClientSearchDialog {
  private _dialog = inject(Dialog);
  open(data: {
    action: string;
    panelClass?: string;
    whiteLabels: any[];
    whiteLabelId: number;
    enableSelectApplication?: boolean;
    enableSelectFunding?: boolean;
    enableSelectClient?: boolean;
    enableMenuOptions?: boolean;
    enableButtonOptions?: boolean;
  }) {
    return this._dialog.open<{
      action: string;
      declineReason?: any;
      application?: any;
      funding?: any;
      client?: any;
    } | null>(OiqClientSearchDialogComponent, {
      panelClass: [
        'oiq-client-search-dialog',
        data.panelClass || 'oiq-client-search-dialog--regular',
      ],
      width: '1024px',
      autoFocus: false,
      restoreFocus: true,
      data,
    }).closed;
  }
}
